/**
 * GuidedTourManager.jsx - Interactive step-by-step tutorial system
 *
 * Features:
 * - Highlights UI elements with spotlight effect
 * - Shows contextual instructions near highlighted elements
 * - Supports multiple position placements
 * - Draggable card so user can move it out of the way
 * - Controls modal opening/closing via context
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGame } from '../context/GameContext';
import { getGuidedTour, GUIDED_TOUR_CONFIG } from '../data/guidedTours';
import '../styles/guided-tour.css';

export function GuidedTourManager({
  currentTour,
  onComplete,
  onSkip,
}) {
  // Get context for modal control
  const { setPendingGuidedTourModal } = useGame();
  const [isVisible, setIsVisible] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  const [cardPosition, setCardPosition] = useState({ top: '50%', left: '50%' });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 }); // User drag offset
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const observerRef = useRef(null);
  const checkIntervalRef = useRef(null);

  const tour = currentTour ? getGuidedTour(currentTour) : null;
  const currentStep = tour?.steps?.[currentStepIndex];
  const totalSteps = tour?.steps?.length || 0;

  // Helper to render text with bold markdown
  const renderText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      // Handle newlines
      if (part.includes('\n')) {
        return part.split('\n').map((line, i) => (
          <React.Fragment key={`${index}-${i}`}>
            {i > 0 && <br />}
            <span>{line}</span>
          </React.Fragment>
        ));
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Reset when tour changes (only reset when starting a NEW tour)
  useEffect(() => {
    if (currentTour) {
      setCurrentStepIndex(0);
      setDragOffset({ x: 0, y: 0 }); // Reset drag position only when tour starts
      setTimeout(() => setIsVisible(true), 50);
      // Clear any pending modal requests when tour starts
      setPendingGuidedTourModal?.(null);
    } else {
      setIsVisible(false);
    }
  }, [currentTour, setPendingGuidedTourModal]);

  // Position card without a target element (for 'left', 'right', 'center', 'sidebar' positions)
  const positionCardWithoutTarget = useCallback((position = 'center') => {
    const cardHeight = 280;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    switch (position) {
      case 'left':
        setCardPosition({
          top: `${viewportHeight / 2}px`,
          left: '40px',
          transform: 'translateY(-50%)',
        });
        break;
      case 'right':
        setCardPosition({
          top: `${viewportHeight / 2}px`,
          right: '40px',
          left: 'auto',
          transform: 'translateY(-50%)',
        });
        break;
      case 'sidebar':
        // Position on the LEFT side of the screen, next to the modal
        setCardPosition({
          top: `${viewportHeight / 2}px`,
          left: '80px',
          transform: 'translateY(-50%)',
        });
        break;
      case 'center':
      default:
        setCardPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
        break;
    }
  }, []);

  // Find and highlight target element
  const updateHighlight = useCallback(() => {
    if (!currentStep?.targetElement) {
      setHighlightRect(null);
      positionCardWithoutTarget(currentStep?.position);
      return;
    }

    const element = document.querySelector(currentStep.targetElement);
    if (!element) {
      setHighlightRect(null);
      // Use step's position even if element not found
      positionCardWithoutTarget(currentStep?.position);
      return;
    }

    const rect = element.getBoundingClientRect();
    const padding = GUIDED_TOUR_CONFIG.highlightPadding;

    setHighlightRect({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Position card relative to element
    positionCard(rect, currentStep.position);
  }, [currentStep]);

  // Position the card near the target element
  const positionCard = (elementRect, position = 'center') => {
    const cardWidth = GUIDED_TOUR_CONFIG.cardMaxWidth;
    const cardHeight = 280; // Approximate height
    const margin = 20;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top, left, transform = '';

    switch (position) {
      case 'top':
        top = elementRect.top - cardHeight - margin;
        left = elementRect.left + elementRect.width / 2;
        transform = 'translateX(-50%)';
        break;
      case 'bottom':
        top = elementRect.bottom + margin;
        left = elementRect.left + elementRect.width / 2;
        transform = 'translateX(-50%)';
        break;
      case 'left':
        top = elementRect.top + elementRect.height / 2;
        left = elementRect.left - cardWidth - margin;
        transform = 'translateY(-50%)';
        break;
      case 'right':
        top = elementRect.top + elementRect.height / 2;
        left = elementRect.right + margin;
        transform = 'translateY(-50%)';
        break;
      case 'bottom-right':
        top = elementRect.bottom + margin;
        left = Math.min(elementRect.right, viewportWidth - cardWidth - margin);
        break;
      case 'bottom-left':
        top = elementRect.bottom + margin;
        left = Math.max(margin, elementRect.left);
        break;
      case 'top-right':
        top = elementRect.top - cardHeight - margin;
        left = Math.min(elementRect.right, viewportWidth - cardWidth - margin);
        break;
      case 'top-left':
        top = elementRect.top - cardHeight - margin;
        left = Math.max(margin, elementRect.left);
        break;
      case 'sidebar':
        // Position on the LEFT side of the screen, next to the modal
        top = viewportHeight / 2;
        left = 80;
        transform = 'translateY(-50%)';
        break;
      case 'center':
      default:
        top = '50%';
        left = '50%';
        transform = 'translate(-50%, -50%)';
        break;
    }

    // Clamp to viewport bounds
    if (typeof top === 'number') {
      top = Math.max(margin, Math.min(top, viewportHeight - cardHeight - margin));
    }
    if (typeof left === 'number') {
      left = Math.max(margin, Math.min(left, viewportWidth - cardWidth - margin));
    }

    setCardPosition({
      top: typeof top === 'number' ? `${top}px` : top,
      left: typeof left === 'number' ? `${left}px` : left,
      transform,
    });
  };

  // Update highlight on step change and window resize
  useEffect(() => {
    updateHighlight();

    const handleResize = () => updateHighlight();
    window.addEventListener('resize', handleResize);

    // Use MutationObserver to detect DOM changes
    if (currentStep?.targetElement) {
      observerRef.current = new MutationObserver(updateHighlight);
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    // Periodic check for element appearance
    checkIntervalRef.current = setInterval(updateHighlight, 500);

    return () => {
      window.removeEventListener('resize', handleResize);
      observerRef.current?.disconnect();
      clearInterval(checkIntervalRef.current);
    };
  }, [currentStep, updateHighlight]);

  // Handle advancing to next step
  const handleNext = useCallback(() => {
    // Check if current step has openModalOnComplete - signal via context
    if (currentStep?.openModalOnComplete) {
      setPendingGuidedTourModal?.(currentStep.openModalOnComplete);
    }

    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Tour complete
      setIsVisible(false);
      setTimeout(() => {
        onComplete?.(currentTour);
      }, GUIDED_TOUR_CONFIG.fadeOutDuration);
    }
  }, [currentStepIndex, totalSteps, currentTour, onComplete, currentStep, setPendingGuidedTourModal]);

  const handlePrevious = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onSkip?.(currentTour);
    }, GUIDED_TOUR_CONFIG.fadeOutDuration);
  }, [currentTour, onSkip]);

  // Dragging handlers
  const handleDragStart = useCallback((e) => {
    // Only drag from header, but not from close button
    if (!e.target.closest('.gt-header') || e.target.closest('.gt-close')) return;

    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: dragOffset.x,
      offsetY: dragOffset.y,
    };
  }, [dragOffset]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;

    setDragOffset({
      x: dragStartRef.current.offsetX + dx,
      y: dragStartRef.current.offsetY + dy,
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Global mouse listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  if (!tour || !currentStep) return null;

  const showFallback = currentStep.fallbackContent && currentStep.targetElement &&
    !document.querySelector(currentStep.targetElement);
  const hasActiveSpotlight = highlightRect && currentStep.highlight;

  // Calculate final card position with drag offset
  const finalCardStyle = {
    ...cardPosition,
    transform: `${cardPosition.transform || ''} translate(${dragOffset.x}px, ${dragOffset.y}px)`.trim(),
  };

  return (
    <>
      {/* Dark overlay - when spotlight active, overlay is transparent and allows clicks through */}
      <div
        className={`gt-overlay ${isVisible ? 'visible' : ''} ${hasActiveSpotlight ? 'has-spotlight' : ''}`}
        onClick={(e) => {
          // Don't close when clicking overlay - tours should be intentional
          e.stopPropagation();
        }}
      />

      {/* Highlight spotlight - the box-shadow creates the darkening effect */}
      {hasActiveSpotlight && (
        <div
          className={`gt-spotlight ${isVisible ? 'visible' : ''} ${currentStep.pulseAnimation ? 'pulse' : ''}`}
          style={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
          }}
        />
      )}

      {/* Tour card - draggable */}
      <div
        className={`gt-card ${isVisible ? 'visible' : ''} ${currentStep.isWarning ? 'warning' : ''} ${currentStep.isComplete ? 'complete' : ''} ${isDragging ? 'dragging' : ''}`}
        style={finalCardStyle}
        onMouseDown={handleDragStart}
      >
        {/* Header - drag handle */}
        <div className="gt-header" style={{ cursor: 'grab' }}>
          <div className="gt-header-left">
            <h3 className="gt-title">{currentStep.title}</h3>
            {GUIDED_TOUR_CONFIG.showProgress && (
              <span className="gt-progress">
                Step {currentStepIndex + 1} of {totalSteps}
              </span>
            )}
          </div>
          {/* Close button (X) - just closes, doesn't skip */}
          <button
            className="gt-close"
            onClick={handleClose}
            aria-label="Close tour"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="gt-body">
          <div className="gt-content">
            {renderText(showFallback ? currentStep.fallbackContent : currentStep.content)}
          </div>
        </div>

        {/* Footer - just Next/Back buttons, no Skip */}
        <div className="gt-footer">
          <div className="gt-footer-left">
            {currentStepIndex > 0 && (
              <button className="gt-btn secondary" onClick={handlePrevious}>
                ← Back
              </button>
            )}
          </div>
          <div className="gt-footer-right">
            <button
              className={`gt-btn primary ${currentStep.isComplete ? 'complete' : ''}`}
              onClick={handleNext}
            >
              {currentStep.actionLabel || (currentStep.isComplete ? 'Finish' : 'Next →')}
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div className="gt-dots">
          {tour.steps.map((_, index) => (
            <span
              key={index}
              className={`gt-dot ${index === currentStepIndex ? 'active' : ''} ${index < currentStepIndex ? 'completed' : ''}`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export default GuidedTourManager;
